import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import CardGrid from '@features/trend-research/components/common/CardGrid';
import {
	Card,
	CardContent,
	ColorThemeImageContainer,
	PrintGraphicsImageContainer,
	TopBar
} from '@features/trend-research/components/common/TrendCard';
import Charts from '@features/trend-research/components/details/ColorGraphicsACP/Charts';
import Graphics from '@features/trend-research/components/details/ColorGraphicsACP/Graphics';
import SolidColor from '@features/trend-research/components/details/ColorGraphicsACP/SolidColor';
import { DataSource, Divider, Product, Tabs } from '@features/trend-research/components/details/components';
import { FilterButtons } from '@features/trend-research/components/details/components/Filters';
import ProductTopSection from '@features/trend-research/components/details/Fabric/ProductTopSection';
import useApi from '@features/trend-research/hooks/useApi';
import useQueryString from '@features/trend-research/hooks/useQueryString';
import {
	_getBasedOnBrandData,
	areArraysEqual,
	cleanUrl,
	colorCategory,
	detailsCountHeaders,
	formatFabricName,
	getDetailsCount,
	graphicsType,
	reverseTabMapping,
	selectedName,
	sourceType,
	tabMapping
} from '@features/trend-research/utils/utils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	findAllBrandInsight,
	findBrandInsight,
	findTrendResearchProductsBySource,
	findTrendResearchUserPreferenceColor
} from '@services/trendResearch';

const ColorGraphicACP = () => {
	const dispatch = useDispatch();
	const [queryStringForApi, setQueryStringForApi] = useState('');
	const queryString = useQueryString();
	const location = useLocation();
	const [selectedItem, setSelectedItem] = useState(null);
	const [page, setPage] = useState(0);
	const [selectedSubCategory, setSelectedSubCategory] = useState([]);
	const [newRelatedProducts, setNewRelatedProducts] = useState([]);
	const { data } = useApi(findTrendResearchUserPreferenceColor, '', queryString);
	const [detailsCount, setDetailsCount] = useState({});
	const prevQueryStringRef = useRef(queryStringForApi);
	const [isClose, setIsClose] = useState(false);
	const [totalPage, setTotalPage] = useState(0);
	const [brands, setBrands] = useState([]);

	const navigate = useNavigate();
	const params = new URLSearchParams(location.search);
	const initialTabType = location?.state?.type;

	const { source } = location?.state || {};

	const [activeTab, setActiveTab] = useState(tabMapping[initialTabType] ?? 0);
	const getAllBrands = async () => {
		try {
			dispatch(openLoader());
			const data = await findAllBrandInsight();
			setBrands(data);

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		getAllBrands();
	}, []);

	useEffect(() => {
		if (initialTabType && tabMapping[initialTabType] !== undefined) {
			setActiveTab(tabMapping[initialTabType]);
		}
	}, [initialTabType]);
	const fetchDetailsCount = async () => {
		try {
			dispatch(openLoader());
			const response = await findBrandInsight('details-count', queryStringForApi);
			setDetailsCount((prev) => ({
				...prev,
				maxPrice: response?.maxPrice,
				midPrice: response?.midPrice,
				minPrice: response?.minPrice
			}));

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		// fetchDetailsCount(queryStringForApi);
		if (
			prevQueryStringRef.current !== queryStringForApi &&
			source !== 'Instagram' &&
			queryStringForApi !== ''
		) {
			fetchDetailsCount(queryStringForApi);
			prevQueryStringRef.current = queryStringForApi; // Update the previous value
		}
	}, [queryStringForApi]);

	useEffect(() => {
		if (queryString !== '') {
			const currentActiveTab = reverseTabMapping[activeTab];

			const searchParams = new URLSearchParams(queryString);

			if (currentActiveTab !== 'key-color') {
				searchParams.append('graphicsType', graphicsType[currentActiveTab]);
			} else {
				searchParams.delete('graphicsType');
			}
			const updatedQueryString = `?${searchParams.toString()}`;

			setQueryStringForApi(updatedQueryString);
		}
	}, [queryString]);

	const fetchProducts = async (queryString, offset = 0) => {
		try {
			dispatch(openLoader());
			if (page <= totalPage) {
				const response = await findTrendResearchProductsBySource(
					sourceType[source],
					queryString + `&page=${offset}&size=20`
				);
				if (!response.data.length) {
					dispatch(closeLoader());
					return;
				}
				setTotalPage(response.totalPages);
				setDetailsCount((prev) => ({
					...prev,
					noOfProducts: response?.totalElements
				}));

				setNewRelatedProducts((prevProducts) => [...prevProducts, ...response.data]);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	useEffect(() => {
		if (queryStringForApi !== '') {
			fetchProducts(queryStringForApi, page);
		}
	}, [page, queryStringForApi]);

	//const location = useLocation();

	useEffect(() => {
		if (location?.state && location?.state?.selectedItem) {
			setSelectedItem(location?.state?.selectedItem);
			setSelectedSubCategory(
				formatFabricName(location?.state?.selectedItem?.[selectedName[initialTabType]])
			);
		}
	}, [location.state]);

	const handleTabClick = (index) => {
		setActiveTab(index);

		const newTabType = reverseTabMapping[index];
		if (newTabType) {
			//params.set('type', newTabType);
			navigate({
				search: params.toString(),
				state: { type: newTabType, source }
			});
		}
		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('graphicsType')) {
			searchParams.delete('graphicsType');
		}
		if (searchParams.has('graphicsContentList')) {
			searchParams.delete('graphicsContentList');
		}
		if (newTabType !== 'key-color') {
			searchParams.delete('colorList');
			if (newTabType) {
				searchParams.append('graphicsType', graphicsType[newTabType]);
			}
		} else if (newTabType === 'key-color') {
			searchParams.delete('graphicsContentList');
			searchParams.delete('graphicsType');
		}

		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
	};

	const handleSelectionChange = (selectedItems) => {
		setSelectedSubCategory(selectedItems);
		const newGraphicsList = selectedItems.join(',');

		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('graphicsContentList')) {
			searchParams.delete('graphicsContentList');
		}
		if (newGraphicsList !== 'All') {
			searchParams.append('graphicsContentList', newGraphicsList);
		}
		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
		window.scrollTo(0, 600);
	};

	const onClose = () => {
		setIsClose(true);
		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('colorList')) {
			searchParams.delete('colorList');
		}
		if (searchParams.has('graphicsContentList')) {
			searchParams.delete('graphicsContentList');
		}

		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
		setSelectedSubCategory([]);
	};

	const handleColorSelect = (selectedColors) => {
		setSelectedSubCategory(selectedColors);
		const newFiberList = selectedColors.join(',');

		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('colorList')) {
			searchParams.delete('colorList');
		}
		if (newFiberList !== 'All') {
			searchParams.append('colorList', newFiberList);
		}
		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
		window.scrollTo(0, 600);
	};

	const handleScroll = async () => {
		if (
			window.innerHeight + document.documentElement.scrollTop + 1 >=
			document.documentElement.scrollHeight
		) {
			setPage((prev) => prev + 1);
		}
	};
	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const [mainImage, ...images] = selectedItem?.imageUrlList?.map(cleanUrl) || [];

	const renderTabContent = () => {
		switch (activeTab) {
			case 0:
				return <SolidColor onSelectionChange={handleColorSelect} />;
			case 1:
				return <Graphics graphicsType="GRAPHICS" onSelectionChange={handleSelectionChange} />;
			case 2:
				return <Graphics graphicsType="AOP" onSelectionChange={handleSelectionChange} />;
			default:
				return null;
		}
	};

	return (
		<>
			{/* <DataSource
				data={getBasedOnBrandData(queryString, data?.productGroupResponseList)}
				headers={brandHeaders}
			/> */}
			{brands?.length > 0 && (
				<CardGrid
					cards={_getBasedOnBrandData(queryString, data?.productGroupResponseList, [], brands)}
				/>
			)}
			<Charts />
			<FilterButtons>
				<div className="category-items mb-3 d-flex flex-wrap justify-content-center align-items-center">
					<Tabs
						tabs={colorCategory}
						activeTab={activeTab}
						onTabClick={handleTabClick}
						additionalClasses="p-0"
					/>
				</div>
				{renderTabContent()}
			</FilterButtons>
			{isClose ||
				(areArraysEqual(
					selectedSubCategory,
					formatFabricName(location?.state?.selectedItem?.[selectedName[initialTabType]])
				) && (
					<div className="key-graphics-section pt-3 trend-padding">
						<div className="graphics-slider grid-column-4">
							<Card className="key-graphics-single edit-key-theme-item">
								<TopBar topText={location?.state?.selectedItem?.isTop} onClose={onClose} />
								{initialTabType === 'key-color' && (
									<ColorThemeImageContainer
										mainImage={mainImage}
										images={
											initialTabType === 'key-color' ? [mainImage, ...images] : images
										}
										themeType={initialTabType === 'key-color' ? 'COLOR' : 'THEME'}
										colorCode={
											initialTabType === 'key-color'
												? location?.state?.selectedItem?.colorHexCode.slice(1, -1)
												: ''
										}
									/>
								)}
								{(initialTabType === 'key-graphics' || initialTabType === 'key-prints') && (
									<PrintGraphicsImageContainer mainImage={mainImage} images={images} />
								)}
								<CardContent
									details={{
										title:
											initialTabType === 'key-color'
												? selectedItem?.colorName
												: selectedItem?.graphicsName,
										itemCount:
											initialTabType === 'key-color'
												? selectedItem?.colorCount
												: selectedItem?.graphicsCount,
										percentage: selectedItem?.percentage
									}}
								/>
							</Card>
						</div>
					</div>
				))}
			<Divider />
			<div className="trending-product border-0 top-fabric-product">
				<DataSource data={getDetailsCount(detailsCount)} headers={detailsCountHeaders} />
				<ProductTopSection title="Related Products" />

				<Product allProducts={newRelatedProducts} />
			</div>
		</>
	);
};

export default ColorGraphicACP;
